import { useState } from "preact/hooks";
import { locale, tr } from "../i18n.js";
import { ApiError, get, html, MARKETING_URL, post } from "../lib.js";
import { ErrorBox, Field, LangToggle, Loading, Logo, Toaster, useAction, useForm, useLoad } from "../ui.js";
import { TermsCheck } from "./billing.js";
import { PageGuide } from "../guide.js";

const COUNTRIES = ["IL", "US", "GB", "CA", "AU", "FR", "DE", "NL", "ES", "IT", "CY", "GR", "PT", "CH", "AT", "BE", "IE", "SE", "PL", "RO", "UA", "RU", "AE", "ZA", "BR", "AR", "MX", "IN"];
const countryName = (code) => {
  try {
    return new Intl.DisplayNames([locale()], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
};

const ils = (n) => `₪${Number(n).toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;

/**
 * Public purchase page, linked from the "Buy" buttons on relayza.company: customer details,
 * terms acceptance, then the secure payment page. Works without signing in.
 */
export function JoinPage() {
  const params = new URLSearchParams(location.search);
  const [data, , error] = useLoad(() => get("/join/plans"), []);
  const { values, bind, setValues } = useForm({
    plan: ["starter", "growth", "pro"].includes(params.get("plan")) ? params.get("plan") : "growth",
    interval: params.get("interval") === "year" ? "year" : "month",
    business_name: "",
    first_name: "",
    last_name: "",
    country: "IL",
    email: "",
    phone: "",
    tax_id: "",
    password: "",
  });
  const [accepted, setAccepted] = useState(false);
  const [exists, setExists] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [busy, run] = useAction();

  if (error) return html`<${ErrorBox} error=${error} />`;
  if (!data) return html`<${Loading} />`;
  const plan = data.plans.find((p) => p.id === values.plan) ?? data.plans[0];
  const price = values.interval === "year" ? plan.annual_ils : plan.monthly_ils;
  const subtotal = price + data.setup_fee_ils;
  const vat = Math.round(subtotal * data.vat_rate * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await run(() => post("/join", { ...values, tax_id: values.tax_id.trim(), accept_terms: true }));
      location.href = res.url;
    } catch (err) {
      if (err instanceof ApiError && err.code === "conflict") setExists(true);
      if (err instanceof ApiError && err.code === "payments_unavailable") setUnavailable(true);
      if (err instanceof ApiError && err.code === "checkout_failed") location.href = "/settings/billing";
    }
  };

  return html`<div class="auth-page">
    <div class="ambient a1" /><div class="ambient a2" /><div class="grid-surface" />
    <div class="auth join">
      <div class="auth-pitch">
        <a href=${MARKETING_URL} aria-label=${tr("RELAYZA website")}><${Logo} big /></a>
        <h1>${tr("Get started with RELAYZA")}</h1>
        <div class="card join-summary">
          <div class="segmented" role="group" aria-label=${tr("Plan")}>
            ${data.plans.map((p) => html`<button type="button" class=${values.plan === p.id ? "active" : ""} onClick=${() => setValues((v) => ({ ...v, plan: p.id }))}>${p.name}</button>`)}
          </div>
          <div class="segmented" role="group" aria-label=${tr("Billing period")}>
            <button type="button" class=${values.interval === "month" ? "active" : ""} onClick=${() => setValues((v) => ({ ...v, interval: "month" }))}>${tr("Monthly")}</button>
            <button type="button" class=${values.interval === "year" ? "active" : ""} onClick=${() => setValues((v) => ({ ...v, interval: "year" }))}>${tr("Yearly · 2 months free")}</button>
          </div>
          <ul class="plain small">
            <li>${tr("{n} AI conversations a month", { n: plan.conversations.toLocaleString() })}</li>
            <li>${plan.channel_types.length === 1 ? tr("WhatsApp + website chat ({n} connected)", { n: plan.channels }) : tr("WhatsApp, Instagram, Messenger + website chat ({n} connected)", { n: plan.channels })}</li>
            <li>${plan.seats ? tr("{n} team members", { n: plan.seats }) : tr("Unlimited team members")}</li>
          </ul>
          <table class="table small"><tbody>
            <tr><td>${values.interval === "year" ? tr("{plan}, first year", { plan: plan.name }) : tr("{plan}, first month", { plan: plan.name })}</td><td>${ils(price)}</td></tr>
            ${data.setup_fee_ils ? html`<tr><td>${tr("Setup and onboarding (one time)")}</td><td>${ils(data.setup_fee_ils)}</td></tr>` : ""}
            <tr><td>${tr("VAT {vat}%", { vat: Math.round(data.vat_rate * 100) })}</td><td>${ils(vat)}</td></tr>
            <tr><td><strong>${tr("Total today")}</strong></td><td><strong>${ils(subtotal + vat)}</strong></td></tr>
          </tbody></table>
          <p class="muted small">${tr("Renews automatically each {period}. Cancel any time; the plan runs to the end of the paid period. Your AI agent is set up with you within 3 business days of payment.", { period: values.interval === "year" ? tr("year") : tr("month") })}</p>
        </div>
        <${PageGuide} id="join" />
      </div>
      <form class="card auth-card" onSubmit=${submit}>
        <h2>${tr("Your details")}</h2>
        <${Field} label=${tr("Business name")}><input required maxlength="100" ...${bind("business_name")} autocomplete="organization" /></${Field}>
        <div class="join-names">
          <${Field} label=${tr("First name")}><input required maxlength="60" ...${bind("first_name")} autocomplete="given-name" /></${Field}>
          <${Field} label=${tr("Last name")}><input required maxlength="60" ...${bind("last_name")} autocomplete="family-name" /></${Field}>
        </div>
        <${Field} label=${tr("Email")} hint=${tr("Also where invoices are sent")}><input type="email" required ...${bind("email")} autocomplete="email" /></${Field}>
        <${Field} label=${tr("Mobile phone")}><input type="tel" required dir="ltr" ...${bind("phone")} autocomplete="tel" /></${Field}>
        <${Field} label=${tr("Country")}><select required ...${bind("country")} autocomplete="country">${COUNTRIES.map((c) => html`<option value=${c}>${countryName(c)}</option>`)}</select></${Field}>
        <${Field} label=${html`${tr("Business number")} (<bdi dir="rtl">ח.פ. / ע.מ.</bdi>)`} hint=${tr("Needed for VAT-deductible invoices")}><input inputmode="numeric" pattern="\\d{8,9}" ...${bind("tax_id")} /></${Field}>
        <${Field} label=${tr("Choose a password")} hint=${tr("At least 8 characters")}><input type="password" required minlength="8" ...${bind("password")} autocomplete="new-password" /></${Field}>
        <${TermsCheck} adult checked=${accepted} onChange=${setAccepted} />
        <button class="btn primary full" disabled=${busy || !accepted}>${busy ? tr("Please wait…") : tr("Continue to secure payment")}</button>
        ${unavailable && html`<div class="callout small">${tr("Online payment is being activated. Email us and we'll complete your purchase:")} <a href="mailto:hello@relayza.company?subject=Relayza">hello@relayza.company</a></div>`}
        ${exists && html`<div class="callout warn small">${tr("This email already has an account.")} <a href="/settings/billing">${tr("Sign in and choose a plan")}</a></div>`}
        <p class="muted center small">${tr("Payment is processed securely by Morning and Grow. We never see your card number.")}</p>
        <p class="muted center small"><${LangToggle} /></p>
      </form>
    </div>
    <${Toaster} />
  </div>`;
}
